import { TypographyH4 } from "@/components/typography/H4";
import { TypographyP } from "@/components/typography/P";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useRenderAppActions } from "../../../hooks/renderAppActions";
import { PartialApp } from "../../../types/app.type";
import AppActions from "../../AppActions";
import GetAppDeploymentStatus from "../../QuickCreateApp/DeployingAppStatus/GetAppDeploymentStatus";
import { GetDeployingStatus } from "../../QuickCreateApp/DeployingAppStatus/GetDeployingStatus";
import AppLink from "./AppLink";

type Props = {
  app: PartialApp;
  disableLink?: boolean;
};

export const AppCard = ({ app, disableLink }: Props) => {
  const { id, displayName, slug, description, pictureUrl, origin } = app;

  const renderAppActions = useRenderAppActions(app);

  const {
    isNewApp,
    isNewAppDisabled,
    isDeployingNewApp,
    isDoneDeployingNewApp,
    isErrorDeployingNewApp
  } = GetDeployingStatus(id);

  return (
    <div className="relative group">
      <AppLink
        app={app}
        disableLink={isNewAppDisabled || isDeployingNewApp || disableLink}
      >
        <div
          data-testid="appOverviewAppCard"
          className={cn(
            "h-44 flex overflow-hidden rounded opacity-90 transition-all duration-75",
            isNewAppDisabled || isDeployingNewApp || disableLink
              ? "cursor-default"
              : "cursor-pointer group-hover:opacity-100",
            isNewApp
              ? "border border-primary dark:border-brand-primary/40 bg-primary/25 dark:bg-brand-primary/20"
              : "bg-brand-gray-50 dark:bg-brand-gray-800"
          )}
        >
          <div className="hidden sm:block relative aspect-square">
            <img
              className="object-cover h-full w-56"
              src={pictureUrl || "/app-placeholder-picture.png"}
              alt={slug}
            />
          </div>

          <div className="relative h-full w-full p-4 flex flex-col justify-between gap-2">
            <div className="relative flex flex-col">
              <TypographyH4
                data-testid="appCardName"
                className="text-brand-gray-800 dark:text-brand-gray-100 line-clamp-1 ml:line-clamp-3 lg:line-clamp-2"
              >
                {displayName}
              </TypographyH4>

              <TypographyP
                data-testid="appCardDescription"
                className="text-brand-gray-700 mt-2 dark:text-brand-gray-200 text-xs leading-4 max-w-sm line-clamp-2 lg:line-clamp-3"
              >
                {description}
              </TypographyP>

              <div className="flex gap-2 mt-4">
                {origin && origin !== "organization" && (
                  <Badge
                    className="pointer-events-none font-normal capitalize bg-brand-primary/30"
                    variant="secondary"
                  >
                    {origin}
                  </Badge>
                )}
              </div>
            </div>
            <GetAppDeploymentStatus
              isNewAppDisabled={isNewAppDisabled}
              isDeployingNewApp={isDeployingNewApp}
              isDoneDeployingNewApp={isDoneDeployingNewApp}
              isErrorDeployingNewApp={isErrorDeployingNewApp}
            />
          </div>
        </div>
      </AppLink>
      {renderAppActions && (
        <div className="absolute top-2 right-0.5 group-hover:right-2 bg-brand-gray-100 shadow-md dark:shadow-none dark:bg-brand-gray-900 p-0.5 rounded-md flex invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all">
          <AppActions app={app} />
        </div>
      )}
    </div>
  );
};
